import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, firstValueFrom} from 'rxjs';
import {Select} from '@ngxs/store';
import {UntilDestroy} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {OrganizationsState} from 'app/state-management/states/organizations.states';
import {Organization} from 'app/models/organization.model';
import {UploadImageRequest} from 'app/models/menu.model';
import {DefaultImage, DefaultImagePositionTypeEnum} from 'app/models/defaultImage.model';
import {DefaultImageApiService} from 'app/services/menu-edit/default-image-api.service';
import {DishesService} from 'app/services/dishes.service';
import {ConfirmDialogComponent} from 'app/shared/components/confirm-dialog/confirm-dialog.component';
import { GetPosPermissionCode } from 'app/helpers/permission-codes';
import { PermissionService } from 'app/services/permission.service';

export interface DefaultImageCard {
  positionType: DefaultImagePositionTypeEnum;
  titleKey: string;
}

@UntilDestroy()
@Component({
  selector: 'app-default-images',
  templateUrl: './default-images.component.html',
  styleUrls: ['./default-images.component.scss']
})
export class DefaultImagesComponent implements OnInit {
  readonly imgDefaultSrc = 'assets/images/dishes/logo.png';

  readonly cards: DefaultImageCard[] = [
    {positionType: DefaultImagePositionTypeEnum.Group, titleKey: 'MENU_EDIT.DEFAULT_IMAGE.CATEGORIES'},
    {positionType: DefaultImagePositionTypeEnum.Product, titleKey: 'MENU_EDIT.DEFAULT_IMAGE.PRODUCTS'},
    {positionType: DefaultImagePositionTypeEnum.Size, titleKey: 'MENU_EDIT.DEFAULT_IMAGE.SIZES'},
  ];

  organization: Organization;
  loading = false;
  error: string | null = null;
  /** Тип позиции, по которому сейчас идёт запись, чтобы блокировать только одну карточку. */
  savingPositionType: DefaultImagePositionTypeEnum | null = null;

  defaultImagesMap: Map<DefaultImagePositionTypeEnum, DefaultImage> = new Map();
  public hasProductEdit$ = this.permissions.hasPermission$(GetPosPermissionCode.GetPosProductEdit);

  @Select(OrganizationsState.getSelectedOrganization)
  selectedOrganization$: Observable<Organization>;

  constructor(
    private defaultImageApiService: DefaultImageApiService,
    private dishesService: DishesService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private permissions: PermissionService,
  ) {
  }

  ngOnInit(): void {
    this.getOrganizationAndData();
  }

  async getOrganizationAndData(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;
      this.organization = await firstValueFrom(this.selectedOrganization$);

      if (!this.organization?.id) {
        throw new Error('Organization not found');
      }

      await this.loadDefaultImages();
    } catch (error) {
      console.error('Failed to initialize default images:', error);
      this.error = this.translate.instant('MENU_EDIT.DEFAULT_IMAGE.LOAD_ERROR');
      this.defaultImagesMap = new Map();
    } finally {
      this.loading = false;
    }
  }

  getDefaultImage(positionType: DefaultImagePositionTypeEnum): DefaultImage | undefined {
    return this.defaultImagesMap.get(positionType);
  }

  getImageSrc(positionType: DefaultImagePositionTypeEnum): string {
    return this.getDefaultImage(positionType)?.imageUrl || this.imgDefaultSrc;
  }

  isCustom(positionType: DefaultImagePositionTypeEnum): boolean {
    return !!this.getDefaultImage(positionType)?.isCustom;
  }

  isSaving(positionType: DefaultImagePositionTypeEnum): boolean {
    return this.savingPositionType === positionType;
  }

  async onImageSelected(event: Event, positionType: DefaultImagePositionTypeEnum): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Сбрасываем input, иначе повторный выбор того же файла не поднимет change.
    input.value = '';

    if (!await firstValueFrom(this.hasProductEdit$) || !file || !this.organization?.id || this.savingPositionType !== null) {
      return;
    }

    try {
      this.savingPositionType = positionType;
      this.error = null;

      const imageBase64 = await this.readFileAsBase64(file);
      const imageUrl = await this.uploadImage(imageBase64);

      if (!imageUrl) {
        throw new Error('Upload returned empty url');
      }

      await this.defaultImageApiService.updateDefaultImageAsync(this.organization.id, positionType, {imageUrl});
      await this.loadDefaultImages();
    } catch (error) {
      console.error('Failed to save default image:', error);
      this.error = this.translate.instant('MENU_EDIT.DEFAULT_IMAGE.SAVE_ERROR');
    } finally {
      this.savingPositionType = null;
    }
  }

  async onResetToStandard(positionType: DefaultImagePositionTypeEnum): Promise<void> {
    if (!await firstValueFrom(this.hasProductEdit$) || !this.organization?.id || this.savingPositionType !== null) {
      return;
    }

    const confirmed = await this.confirmReset();
    if (!confirmed) {
      return;
    }

    try {
      this.savingPositionType = positionType;
      this.error = null;
      await this.defaultImageApiService.resetDefaultImageAsync(this.organization.id, positionType);
      await this.loadDefaultImages();
    } catch (error) {
      console.error('Failed to reset default image:', error);
      this.error = this.translate.instant('MENU_EDIT.DEFAULT_IMAGE.RESET_ERROR');
    } finally {
      this.savingPositionType = null;
    }
  }

  private async confirmReset(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: this.translate.instant('MENU_EDIT.DEFAULT_IMAGE.CONFIRM_RESET_TITLE'),
        message: this.translate.instant('MENU_EDIT.DEFAULT_IMAGE.CONFIRM_RESET'),
        confirmLabel: 'MENU_EDIT.DEFAULT_IMAGE.RESET',
      },
    });

    return !!await firstValueFrom(dialogRef.afterClosed());
  }

  private async loadDefaultImages(): Promise<void> {
    const defaultImages = await this.defaultImageApiService.getDefaultImagesAsync(this.organization.id);
    const map = new Map<DefaultImagePositionTypeEnum, DefaultImage>();
    for (const defaultImage of defaultImages ?? []) {
      map.set(defaultImage.positionType, defaultImage);
    }
    this.defaultImagesMap = map;
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = (e.target?.result ?? '') as string;
        resolve(result.replace('data:', '').replace(/^.+,/, ''));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private async uploadImage(imageBase64: string): Promise<string> {
    const uploadImageRequest: UploadImageRequest = {
      organizationId: this.organization.id,
      imageBase64,
      IsDeletedPicture: false,
    };

    const response = await firstValueFrom(this.dishesService.uploadImage$(uploadImageRequest));
    return response?.data ?? '';
  }
}
